import React, { useState } from 'react';
import { toast } from 'react-toastify';
import LeaseApiService from '../../services/apiService';
import { useTranslation } from 'react-i18next';
import { unitMediaUrls } from './unitImageHelpersRA';
import MediaPreviewModal, { MEDIA_ACCEPT, fileKind, isAllowedMedia } from './mediaPreview';

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;
const MAX_FILE_SIZE_MB = 200;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ImagesList = ({ attachments, setDeletedFiles, deletedFiles, setAttachments, createEmptyRow }) => {
    const { t } = useTranslation();
    const apiService = new LeaseApiService();

    // Attachment
    const [preview, setPreview] = useState(null);
    let readOnly = false
    // Attachment Functions
    // Adds every selected file. With a rowId (the Upload button of one row) the first file fills that row;
    // all other files, and every file from the bulk picker, get a row of their own.
    const handleFilesSelect = (fileList, rowId) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const valid = files.filter((file) => {
            if (!isAllowedMedia(file)) {
                toast.error(`${file.name}: ${t('unitMaster.imagesFields.unsupportedType', 'Only images, PDF files and videos can be uploaded.')}`);
                return false;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                toast.error(`${file.name}: ${t('common.fileTooLarge', `File exceeds the ${MAX_FILE_SIZE_MB}MB limit and was not added.`)}`);
                return false;
            }
            return true;
        });
        if (!valid.length) return;
        const fill = (row, file) => ({ ...row, file, fileName: file.name, isNew: true });
        setAttachments((prev) => {
            const queue = [...valid];
            let rows = prev;
            if (rowId !== undefined) rows = prev.map((row) => (row.id === rowId ? fill(row, queue.shift()) : row));
            // drop the blank rows, add one row per remaining file and keep a single blank row at the end
            rows = rows.filter((row) => row.fileName || row.remarks);
            return [...rows, ...queue.map((file) => fill(createEmptyRow(), file)), createEmptyRow()];
        });
    };
    const handleRemarkChange = (id, value) => {
        setAttachments((prev) =>
            prev.map((row) =>
                row.id === id ? { ...row, remarks: value } : row
            )
        );
    };
    const handleDelete = async (row) => {
        if (!row.isNew) {
            // Deletion isn't a separate API call here - it only takes effect
            // once Save sends this row back with isDeleted:true and its real
            // record_Id, which the backend's spUnitImage_Delete needs. Keep
            // the full row (not just the filename) so record_Id survives to
            // that point - it previously only stored the filename, which
            // meant the backend could never actually be told to delete
            // anything (Record_Id was always missing/0).
            setDeletedFiles((prev) => [...prev, row]);
        }

        setAttachments((prev) => prev.filter((item) => item.id !== row.id));
    };
    const closePreview = () => {
        setPreview((prev) => {
            if (prev?.revoke) URL.revokeObjectURL(prev.url);
            return null;
        });
    };
    const handleView = (row) => {
        if (!row) return;
        if (row.isNew) {
            if (!row.file) return;
            setPreview({ url: URL.createObjectURL(row.file), name: row.fileName, kind: fileKind(row.file.name, row.file.type), revoke: true });
        } else {
            const urls = unitMediaUrls(row.fullUrl);
            setPreview({ url: urls[0], urls, name: row.fileName, kind: fileKind(row.fileName, '', row.fullUrl) });
        }
    };
    return (
        <div className=''>
            <div className="d-flex justify-content-end mb-2">
                <input
                    type="file"
                    accept={MEDIA_ACCEPT}
                    multiple
                    hidden
                    id="unit-files-bulk"
                    onChange={(e) => {
                        handleFilesSelect(e.target.files);
                        e.target.value = "";
                    }}
                />
                <button type="button" className="mainbtn" onClick={() => document.getElementById("unit-files-bulk").click()}>
                    {t('unitMaster.imagesFields.addFiles', 'Upload multiple files')}
                </button>
            </div>
            <table className="table table-bordered">
                <thead className="dynoTable">
                    <tr>
                        <th>{t('common.fileName')}</th>
                        <th>{t('common.remarks')} *</th>
                        <th>{t('common.upload')}</th>
                        <th>{t('common.view')}</th>
                        <th>{t('common.delete')}</th>
                    </tr>
                </thead>
                <tbody>
                    {attachments.map((row) => (
                        <tr key={row.id}>
                            <td>
                                <input disabled value={row.fileName} className="form-control" />
                            </td>

                            <td>
                                <input
                                    disabled={readOnly}
                                    value={row.remarks}
                                    required
                                    placeholder={row.fileName ? t('unitMaster.imagesFields.remarksRequired', 'Required') : ''}
                                    className={`form-control${row.isNew && row.file && !String(row.remarks || '').trim() ? ' is-invalid' : ''}`}
                                    onChange={(e) =>
                                        handleRemarkChange(row.id, e.target.value)
                                    }
                                />
                            </td>

                            <td>
                                <input
                                    type="file"
                                    accept={MEDIA_ACCEPT}
                                    multiple
                                    hidden
                                    id={`file-${row.id}`}
                                    onChange={(e) => {
                                            handleFilesSelect(e.target.files, row.id);
                                            e.target.value = "";
                                        }}
                                />
                                <button
                                    className="mainbtn"
                                    onClick={() =>
                                        document.getElementById(`file-${row.id}`).click()
                                    }
                                >
                                    {t('common.upload')}
                                </button>
                            </td>

                            <td>
                                <button
                                    className="mainbtnRed"
                                    disabled={!row.fileName}
                                    onClick={() => handleView(row)}
                                >
                                    {t('common.view')}
                                </button>
                            </td>

                            <td>
                                <button
                                    className="mainbtnSecondary"
                                    onClick={() => handleDelete(row)}
                                >
                                    {t('common.delete')}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <MediaPreviewModal preview={preview} onHide={closePreview} />
        </div>
    )
}

export default ImagesList